import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { authGet, authPut } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 1400, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  field: {
    marginBottom: theme.spacing(4),
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  sublabel: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  row: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "stretch",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  textareaBox: { flex: "0 0 33.333%", display: "flex", flexDirection: "column" },
  previewBox: {
    flex: "0 0 66.666%",
    border: "1px solid rgba(0,0,0,0.15)",
    borderRadius: 4,
    overflow: "hidden",
    minHeight: 100,
    backgroundColor: "#fafafa",
  },
  previewLabel: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5, 1),
    backgroundColor: "#f0f0f0",
    borderBottom: "1px solid rgba(0,0,0,0.1)",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  iframe: {
    width: "100%",
    border: "none",
    minHeight: 100,
    display: "block",
  },
  actions: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
}));

function buildHeaderAdHtml(content) {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    /* background: linear-gradient(90deg, #1a3561 0%, #2a5298 50%, #1a3561 100%); */
    background: #FFF;
    margin-top: 10px;
    font-family: Arial, Helvetica, sans-serif;
  }
  .header-wrap {
    display: flex;
    align-items: center;
    padding: 10px 20px;
    min-height: 70px;
  }
  .col-logo {
    flex: 0 0 25%;
  }
  .col-logo img {
    max-height: 52px;
    display: block;
  }
  .col-announcement {
    flex: 0 0 50%;
    text-align: center;
    color: #767676;
    padding: 0 12px;
  }
  .col-search {
    flex: 0 0 25%;
    display: flex;
    justify-content: flex-end;
    border-radius: 3px;
    border: 1px solid #ccc;
    height: 38px;
  }
  .search-wrap {
    display: flex;
    align-items: center;
    background: #fff;
    border-radius: 3px;
    overflow: hidden;
    width: 100%;
    max-width: 220px;
  }
  .search-wrap input {
    border: none;
    outline: none;
    padding: 4px 8px;
    font-size: 12px;
    flex: 1;
    color: #333;
    height: 31px;
  }
  .search-wrap button {
    border: none;
    padding: 4px 8px;
    cursor: default;
    background: none;
  }
</style>
</head>
<body>
  <div class="header-wrap">
    <div class="col-logo">
      <img src="https://www.prestigetime.com/img/logo.png" alt="Prestige Time" />
    </div>
    <div class="col-announcement">${content || "<span style='opacity:0.4;font-size:13px'>Header ad content will appear here</span>"}</div>
    <div class="col-search">
      <div class="search-wrap">
        <input type="text" placeholder="Search by keyword or model #" />
        <button>&#128269;</button>
      </div>
    </div>
  </div>
</body>
</html>`;
}

function buildMobileHeaderHtml(content) {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #fff; font-family: Arial, Helvetica, sans-serif; }
  #mobile-banner {
    background: linear-gradient(135deg, #053046 0%, #0a5683 100%);
    color: #fff;
    font-size: 13px;
    padding: 10px;
    text-align: center;
  }
  #mobile-search {
    padding: 8px 10px;
    border-top: 1px solid #e0e0e0;
  }
  .search-wrap {
    display: flex;
    border: 1px solid #ccc;
    border-radius: 3px;
    overflow: hidden;
    background: #fff;
  }
  .search-wrap input {
    flex: 1;
    border: none;
    outline: none;
    padding: 7px 10px;
    font-size: 13px;
    color: #333;
  }
  .search-wrap button {
    background: #f0f0f0;
    border: none;
    border-left: 1px solid #ccc;
    padding: 7px 10px;
    cursor: default;
    color: #555;
  }
  
  #mobile-logo-banner{
    margin: 8px 0;
  }

  .mobile-logo {
    max-height:39px; 
    margin-left:auto; 
    margin-right:auto;
  }
  
  .img-responsive {
    display: block;
    max-width: 100%;
    height: auto; 
  }

</style>
</head>
<body>
  <div id="mobile-logo-banner">
     <img src="/img/logo.png" alt="Prestige Time Logo" class="img-responsive mobile-logo">
  </div>
  <div id="mobile-banner">
    ${content || "<span style='opacity:0.45'>Mobile banner content will appear here</span>"}
  </div>
  <div id="mobile-search">
    <div class="search-wrap">
      <input type="text" placeholder="Search by Keyword or Model #" />
      <button>&#128269;</button>
    </div>
  </div>
</body>
</html>`;
}

function HtmlPreview({ 
  html, 
  height = 200, 
  fullHeader = false, 
  mobile = false,
  width = "100%",
  center = false
}) {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(
      fullHeader ? buildHeaderAdHtml(html) :
      mobile ? buildMobileHeaderHtml(html) :
      (html || "<p style='color:#999;font-family:sans-serif;padding:16px'>No content yet</p>")
    );
    doc.close();
  }, [html, fullHeader, mobile]);

  return (
    <iframe
      ref={iframeRef}
      style={{
        width,
        margin: center ? "0 auto" : undefined,
        border: "none",
        minHeight: height,
        display: "block"
      }}
      title="preview"
    />
  );
}

export function HeaderPage() {
  const classes = useStyles();
  const siteId = useSelector((state) => state.scopes.site_id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "" });

  const [headerAd, setHeaderAd] = useState("");
  const [headerAdMobile, setHeaderAdMobile] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");

  useEffect(() => {
    fetchHeader();
  }, [siteId]);

  async function fetchHeader() {
    setLoading(true);
    const { data, error } = await authGet(["/site-header", { site: siteId }]);
    setLoading(false);
    if (error) { setSnack({ open: true, msg: error?.message || "Failed to load header data" }); return; }
    setHeaderAd(data?.header_ad || "");
    setHeaderAdMobile(data?.header_ad_mobile || "");
    setHeaderMessage(data?.header_message || "");
  }

  async function handleSave() {
    setSaving(true);
    const { error } = await authPut(["/site-header", { site: siteId }], {
      header_ad: headerAd,
      header_ad_mobile: headerAdMobile,
      header_message: headerMessage,
    });
    setSaving(false);
    if (error) { setSnack({ open: true, msg: error?.message || "Failed to save" }); return; }
    setSnack({ open: true, msg: "Saved" });
  }

  if (loading) return <Box className={classes.root}><CircularProgress size={24} /></Box>;

  return (
    <Box className={classes.root}>
      <Typography variant="h6" style={{ marginBottom: 2 }}>Header / Banner</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
        Edit banner HTML for each placement. The preview renders the HTML exactly as it will appear on the frontend.
      </Typography>

      <Box className={classes.field}>
        <Typography className={classes.label} style={{ marginBottom: 5 }}>Header Ad (Desktop)</Typography>
        {/** <Typography className={classes.sublabel}>Displayed in the desktop site header banner.</Typography> */}
        <Box className={classes.row}>
          <Box className={classes.textareaBox}>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              value={headerAd}
              onChange={(e) => setHeaderAd(e.target.value)}
              style={{ flex: 1 }}
              InputProps={{ style: { height: "100%", alignItems: "flex-start" } }}
              inputProps={{ style: { height: "100%", overflow: "auto", resize: "none", fontFamily: "monospace", fontSize: "0.82rem" } }}
            />
          </Box>
          <Paper className={classes.previewBox} elevation={0} style={{ minHeight: 115 }}>
            <div className={classes.previewLabel}>Preview</div>
            <HtmlPreview html={headerAd} height={90} fullHeader />
          </Paper>
        </Box>
      </Box>

      {/**
       * 
       * 
       <Divider style={{ marginBottom: 32 }} />
        */}

      <Box className={classes.field}>
        <Typography className={classes.label} style={{ marginBottom: 5 }}>Mobile Header Ad</Typography>
          {/** <Typography className={classes.sublabel}>Displayed in the mobile site header banner.</Typography> */}
        <Box className={classes.row}>
          <Box className={classes.textareaBox}>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              value={headerAdMobile}
              onChange={(e) => setHeaderAdMobile(e.target.value)}
              style={{ flex: 1 }}
              InputProps={{ style: { height: "100%", alignItems: "flex-start" } }}
              inputProps={{ style: { height: "100%", overflow: "auto", resize: "none", fontFamily: "monospace", fontSize: "0.82rem" } }}
            />
          </Box>
          <Paper className={classes.previewBox} elevation={0} style={{ minHeight: 165 }}>
            <div className={classes.previewLabel}>Preview</div>
            <HtmlPreview html={headerAdMobile} height={170} mobile width="35%" center />
          </Paper>
        </Box>
      </Box>

      {/**
       * 
       * 
       <Divider style={{ marginBottom: 32 }} />
        */}

      <Box className={classes.field}>
        <Typography className={classes.label} style={{ marginBottom: 5 }}>Preowned Header Message</Typography>
          {/** <Typography className={classes.sublabel}>Displayed in the preowned watches site header.</Typography> */}
        <Box className={classes.row}>
          <Box className={classes.textareaBox}>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              value={headerMessage}
              onChange={(e) => setHeaderMessage(e.target.value)}
              style={{ flex: 1 }}
              InputProps={{ style: { height: "100%", alignItems: "flex-start" } }}
              inputProps={{ style: { height: "100%", overflow: "auto", resize: "none", fontFamily: "monospace", fontSize: "0.82rem" } }}
            />
          </Box>
          <Paper className={classes.previewBox} elevation={0} style={{ minHeight: 150 }}>
            <div className={classes.previewLabel}>Preview</div>
            <HtmlPreview html={headerMessage} height={150} />
          </Paper>
        </Box>
      </Box>

      <Box className={classes.actions}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}